/**
 * GPS Store — géolocalisation réelle du joueur.
 *
 * Expose :
 *   gpsState.location      [lat, lng] | null
 *   gpsState.accuracy      précision en mètres
 *   gpsState.status        'idle' | 'acquiring' | 'ok' | 'error' | 'denied'
 *   gpsState.error         message d'erreur lisible
 *
 * startGPS()   — démarre watchPosition (haute précision)
 * stopGPS()    — arrête le watcher
 * getLocation() — renvoie une Promise<[lat,lng]> (résolution unique)
 */

import { reactive } from 'vue';

export const gpsState = reactive({
  location: null as [number, number] | null,
  accuracy: null as number | null,
  /** Altitude WGS84 fournie par le terminal. Nullable et jamais utilisée
   * seule pour décider d'un étage. */
  altitude: null as number | null,
  altitudeAccuracy: null as number | null,
  status: 'idle',
  error: null as string | null,
  lastUpdated: null as number | null,
});

let watchId: number | null = null;

const GEO_OPTIONS = {
  enableHighAccuracy: true,
  timeout: 15000,
  maximumAge: 5000,
};

function onSuccess(pos: GeolocationPosition) {
  const { latitude, longitude, accuracy, altitude, altitudeAccuracy } = pos.coords;
  gpsState.location = [latitude, longitude];
  gpsState.accuracy = Math.round(accuracy);
  gpsState.altitude = Number.isFinite(altitude) ? altitude : null;
  gpsState.altitudeAccuracy = Number.isFinite(altitudeAccuracy) ? altitudeAccuracy : null;
  gpsState.status = 'ok';
  gpsState.error = null;
  gpsState.lastUpdated = Date.now();
  window.dispatchEvent(new CustomEvent('guild-gps-update', {
    detail: { lat: latitude, lng: longitude, accuracy, altitude, altitudeAccuracy }
  }));
}

function onError(err: GeolocationPositionError) {
  gpsState.status = err.code === 1 ? 'denied' : 'error';
  gpsState.error = err.code === 1
    ? 'Géolocalisation refusée. Activez le GPS dans les paramètres.'
    : err.code === 3
      ? 'GPS : délai dépassé. Vérifiez votre connexion.'
      : `Erreur GPS (code ${err.code})`;
  console.warn('[GPS]', gpsState.error);
}

export function startGPS() {
  if (!navigator.geolocation) {
    gpsState.status = 'error';
    gpsState.error = 'Géolocalisation non supportée par ce navigateur.';
    return;
  }
  if (watchId !== null) return; // Déjà démarré

  gpsState.status = 'acquiring';

  // Premier fix rapide
  navigator.geolocation.getCurrentPosition(onSuccess, onError, GEO_OPTIONS);

  // Puis suivi continu
  watchId = navigator.geolocation.watchPosition(onSuccess, onError, GEO_OPTIONS);
}

export function stopGPS() {
  if (watchId !== null && navigator.geolocation) {
    navigator.geolocation.clearWatch(watchId);
    watchId = null;
  }
  gpsState.status = 'idle';
}

/**
 * Résout dès qu'un fix GPS est disponible (ou rejette après timeout).
 * @param {number} timeoutMs
 * @returns {Promise<[number, number]>}
 */
export function getLocation(timeoutMs = 15000) {
  if (gpsState.status === 'ok' && gpsState.location) {
    return Promise.resolve(gpsState.location);
  }
  return new Promise((resolve, reject) => {
    const cleanup = () => {
      clearTimeout(tid);
      window.removeEventListener('guild-gps-update', handler);
    };
    const handler = (e: any) => {
      cleanup();
      resolve([e.detail.lat, e.detail.lng]);
    };
    const tid = setTimeout(() => {
      cleanup();
      reject(new Error('GPS timeout'));
    }, timeoutMs);
    window.addEventListener('guild-gps-update', handler, { once: true });
    if (gpsState.status === 'idle') startGPS();
  });
}

/**
 * Génère des coordonnées déterministes autour d'une position centrale.
 * Chaque quête obtient un offset stable basé sur son id (pas de randomisation à chaque render).
 *
 * @param {string} questId
 * @param {number} centerLat
 * @param {number} centerLng
 * @param {number} radiusM  - rayon max en mètres (défaut 500)
 * @returns {{ lat: number, lng: number }}
 */
export function questCoordinatesAround(questId: string, centerLat: number, centerLng: number, radiusM = 500) {
  // Hash stable à partir de l'id de la quête
  let hash = 0;
  for (let i = 0; i < questId.length; i++) {
    hash = ((hash << 5) - hash) + questId.charCodeAt(i);
    hash |= 0;
  }
  const h1 = Math.abs(hash) / 2147483647;
  const h2 = Math.abs((hash * 2654435761) >>> 0) / 4294967296;

  // Distribue en cercle, pas en carré, pour une vraie répartition spatiale
  const angle = h1 * 2 * Math.PI;
  const r = Math.sqrt(h2) * radiusM; // sqrt pour distribuer uniformément dans le disque

  const dLat = (r * Math.cos(angle)) / 111320;
  const dLng = (r * Math.sin(angle)) / (111320 * Math.cos(centerLat * Math.PI / 180));

  return {
    lat: centerLat + dLat,
    lng: centerLng + dLng,
  };
}


/**
 * Portee d'interaction, en metres.
 *
 * Distance maximale pour engager un joueur, un monstre ou une quete. C'etait
 * ecrit en dur a huit endroits, dont DEUX a 10 m au lieu de 100 (le clic sur un
 * monstre, en 2D comme en 3D) : le message annoncait « approchez-vous a moins
 * de 100 m » alors que l'attaque restait refusee au-dela de 10. Un zero perdu
 * en chemin, jamais rattrape parce que la valeur etait recopiee partout au lieu
 * d'etre nommee.
 *
 * Une seule source desormais. Changer la portee du jeu se fait ici.
 */
export const PORTEE_INTERACTION_M = 100;
